/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Project } from '@ips-jit/schema';
import { Box, Button, Chip, Popover, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { AgentStatus, needsLogin, TONE_COLOR, worstTone } from './AgentStatus';
import { ChevronIcon, DotIcon } from './icons';
import { PLATFORM_LABEL } from './platform';

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Typography component="li" variant="body2">
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {label}:
    </Box>{' '}
    {value}
  </Typography>
);

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** The runtime in a few words: its name and whether its agent is there. */
export const runtimeLabel = (device: Device): string =>
  `${device.name} · ${
    device.status === 'online'
      ? 'online'
      : device.last_seen
        ? `offline, last seen ${clock(device.last_seen)}`
        : 'never connected'
  }`;

export interface ProjectHeaderProps {
  project: Project;
  devices: Device[];
  deviceId: string;
  onDeviceChange: (deviceId: string) => void;
}

/**
 * One line over the project: its name and the runtime it is built for. Each opens what is behind it: the project's
 * facts, and what the runtime's agent reports about itself, the app and the cameras.
 */
export const ProjectHeader = ({ project, devices, deviceId, onDeviceChange }: ProjectHeaderProps) => {
  const [open, setOpen] = useState<{ which: 'project' | 'runtime'; anchor: HTMLElement }>();
  const chosen = devices.find((device) => device.device_id === deviceId);
  const tone = chosen ? worstTone(chosen) : undefined;
  const close = () => setOpen(undefined);

  return (
    <Stack
      component="header"
      aria-label="Project"
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', minWidth: 0, minHeight: 32 }}
    >
      <Button
        color="inherit"
        aria-haspopup="dialog"
        endIcon={<ChevronIcon direction="down" fontSize="small" />}
        onClick={(event) => setOpen({ which: 'project', anchor: event.currentTarget })}
        size="small"
        sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, ml: -1 }}
      >
        <Typography variant="h6" component="h1" noWrap sx={{ fontSize: 17, lineHeight: 1.4 }}>
          {project.name}
        </Typography>
      </Button>
      <Chip
        size="small"
        variant="outlined"
        aria-haspopup="dialog"
        aria-label={`Runtime: ${chosen ? runtimeLabel(chosen) : project.runtime_id ? 'deleted' : 'not chosen'}`}
        color={chosen ? 'default' : 'warning'}
        icon={<DotIcon sx={{ fontSize: 14, '&&': { color: tone ? TONE_COLOR[tone] : 'warning.main' } }} />}
        label={
          chosen
            ? runtimeLabel(chosen)
            : project.runtime_id
              ? 'The runtime of this project was deleted'
              : 'Choose a runtime'
        }
        onClick={(event) => setOpen({ which: 'runtime', anchor: event.currentTarget })}
        sx={{ minWidth: 0 }}
      />
      <Popover
        open={open !== undefined}
        anchorEl={open?.anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 340 } } }}
      >
        {open?.which === 'project' && (
          <Stack component="ul" spacing={0.75} aria-label="Project facts" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            <Fact label="Name" value={project.name} />
            <Fact label="Platform" value={PLATFORM_LABEL[project.platform]} />
            <Fact label="Cameras" value={project.camera_models.join(', ') || '—'} />
            <Fact label="Created" value={new Date(project.created_at).toLocaleString()} />
          </Stack>
        )}
        {open?.which === 'runtime' && (
          <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Runtime
            </Typography>
            {project.runtime_id === null && (
              <TextField
                select
                label="Runtime"
                size="small"
                value={deviceId}
                onChange={(event) => onDeviceChange(event.target.value)}
                // The browser's own list: it is not a popup over the page, so nothing the page does can close it.
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                helperText={
                  devices.length === 0
                    ? 'Add a runtime on the Runtimes page first; the app is built for one runtime.'
                    : 'The app is built for this runtime and sent to it when you approve a plan.'
                }
              >
                {devices.length === 0 && <option value="">No device</option>}
                {devices.map((device) => (
                  <option key={device.device_id} value={device.device_id}>
                    {device.name} ({device.status})
                  </option>
                ))}
              </TextField>
            )}
            {chosen && needsLogin(chosen) && (
              <Button
                component={RouterLink}
                to={`/runtimes?edit=${chosen.device_id}`}
                variant="outlined"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add login
              </Button>
            )}
            {chosen ? (
              <AgentStatus device={chosen} />
            ) : (
              project.runtime_id !== null && (
                <Typography variant="body2">The runtime of this project was deleted.</Typography>
              )
            )}
          </Stack>
        )}
      </Popover>
    </Stack>
  );
};
